import {
  getMappedName,
  getName,
} from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { formatAddress as formatAddressLead } from "@rentiohq/shared-frontend/dist/reduxInsurance/lead/lead.utils";
import { IActivityItem } from "@rentiohq/shared-frontend/dist/types/activity.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  mapActivitySubtypeToString,
  mapActivityTypeToString,
} from "@rentiohq/shared-frontend/dist/utils/activity.utils";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/utils/paymentOrder.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { capitalize } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import { Typography } from "@rentiohq/web-shared-next/dist/ui-components";
import { ActivityTextStyles } from "./ActivityText.styles";

interface IProps {
  activityItem: IActivityItem;
  onParamPress?: (param: string) => void;
}

export const ActivityText = ({ activityItem, onParamPress }: IProps) => {
  const sx = ActivityTextStyles();

  const renderFallbackInfo = () => getLocalizedText("activity.not_supported");

  const renderTouchableActivityText = (
    activityItem: IActivityItem,
    nonTouchableValues: {},
    touchableValues: { [key: string]: string | undefined },
  ) => {
    const { type, subtype, lokaliseKey } = activityItem;

    let key = `activity.${mapActivityTypeToString(
      type,
      false,
    )}.${mapActivitySubtypeToString(subtype, false)}`.toLowerCase();

    if (lokaliseKey && getLocalizedText(lokaliseKey) !== lokaliseKey) {
      key = lokaliseKey;
    }

    const baseString = getLocalizedText(key, nonTouchableValues);
    const stringComponents = baseString.split(/{{|}}/);

    return (
      <>
        {stringComponents.map((stringComponent, index) => {
          // TODO: Render touchable values here
          if (index === 0) {
            return capitalize(stringComponent);
          }

          return stringComponent;
        })}
      </>
    );
  };

  const renderContent = () => {
    const {
      actor,
      amount,
      application,
      asset,
      broker,
      contact,
      contract,
      contractNewPrice,
      contractOldPrice,
      contractRentDiscountPrice,
      dateOfCancellation,
      datetime,
      documentFilename,
      documentPackage,
      email,
      employee,
      epcExpirationDate,
      epcLabel,
      epcValue,
      invitingAccount,
      invoiceAmount,
      lead,
      linkedPropertyId,
      locationDescriptionRejectionComment,
      mainPropertyId,
      newStopDate,
      notificationType,
      oldStopDate,
      participant,
      payee,
      payeeBankAccount,
      payer,
      payerBankAccount,
      paymentOrder,
      paymentPayIn,
      paymentPayInRemainingBalance,
      paymentRequest,
      paymentTransaction,
      property,
      propertyInfo,
      quote,
      reasonForCancellation,
      reference,
      rentAmount,
      rentContractRejectionComment,
      roles,
      source,
      step,
      stopDate,
      subtype,
      tacVersion,
      taskTitle,
      type,
      usedSigningType,
      visitUrl,
    } = activityItem;

    const getLinkedProperty = (contract?: IContract) => {
      if (!linkedPropertyId || !contract?.linkedProperties) {
        return;
      }

      if (contract.linkedProperties?.length > 0) {
        const targetProperty = contract.linkedProperties.find(
          (item: any) => item.linkedPropertyId === linkedPropertyId,
        );

        // @ts-ignore
        return formatAddress(targetProperty?.linkedProperty);
      }

      return;
    };

    const getMainProperty = (contract?: IContract) => {
      if (!mainPropertyId || !contract || !contract?.property) {
        return;
      }

      return formatAddress(contract.property);
    };

    if (
      mapActivityTypeToString(type, false) === undefined ||
      mapActivitySubtypeToString(subtype, false) === undefined
    ) {
      return renderFallbackInfo();
    }

    let paymentType = "invalid payment order";
    if (paymentOrder) {
      paymentType = paymentOrderUtils.getTitle(paymentOrder);
    }

    let paymentRequestPeriod = "invalid payment request";
    if (paymentRequest) {
      if (paymentRequest.startedAt === paymentRequest.endedAt) {
        paymentRequestPeriod = formatDate(paymentRequest.startedAt);
      } else {
        paymentRequestPeriod = `${formatDate(
          paymentRequest.startedAt,
        )} - ${formatDate(paymentRequest.endedAt)}`;
      }
    }

    let paymentRequestEstimatedCollectionDate = "invalid payment request";
    if (paymentRequest?.estimatedCollectionDate) {
      paymentRequestEstimatedCollectionDate = formatDate(
        paymentRequest.estimatedCollectionDate,
      );
    }

    let paymentRequestTitle = "invalid payment request";
    if (paymentRequest) {
      paymentRequestTitle = `${paymentRequestUtils.getTitle(
        paymentRequest,
      )} (${paymentRequestPeriod})`;
    }

    let actorFormatted = actor
      ? getMappedName(actor, false)
      : getLocalizedText("system.rentio");
    if (employee?.firstname || employee?.lastname) {
      actorFormatted = `${actorFormatted} (${[
        employee.firstname,
        employee.lastname,
      ]
        .filter(Boolean)
        .join(" ")})`;
    }

    const validStopDate = (date: Date) => {
      if (typeof date === "string") {
        return new Date(date) || contract?.stopDate;
      } else {
        return date;
      }
    };

    const values = {
      createdAtDateAndTime: datetime
        ? formatDate(datetime, "dd/MM/yyyy HH:mm")
        : "invalid datetime",
      createdAtDate: datetime ? formatDate(datetime) : "invalid datetime",
      createdAtTime: datetime
        ? formatDate(datetime, "HH:mm")
        : "invalid datetime",
      actor: actorFormatted,
      amount: amount ? formatCurrency(amount) : "invalid amount",
      application: application
        ? getName(application.contacts[0])
        : getLocalizedText("activity.param_not_available.application"),
      applicationEmail: application?.applicationEmail || "No email",
      asset: asset ? asset.name : "invalid asset",
      broker: broker ? broker.name : "invalid broker",
      contact: contact ? getName(contact) : "invalid contact",
      contract: contract
        ? getLocalizedText(
            `contract.type.${contract.contractType}`,
          ).toLowerCase()
        : "invalid contract",
      contractNewPrice: contractNewPrice
        ? formatCurrency(contractNewPrice)
        : "invalid amount",
      contractOldPrice: contractOldPrice
        ? formatCurrency(contractOldPrice)
        : "invalid amount",
      contractRentDiscountPrice: contractRentDiscountPrice
        ? formatCurrency(contractRentDiscountPrice)
        : "invalid amount",
      documentFilename: documentFilename
        ? documentFilename
        : "invalid document filename",
      documentPackage: documentPackage
        ? documentPackage.name
        : "invalid document package",
      iban: payeeBankAccount ? payeeBankAccount.iban : "invalid iban",
      payeeBankAccount: payeeBankAccount
        ? payeeBankAccount.iban
        : "invalid iban",
      payerBankAccount: payerBankAccount
        ? payerBankAccount.iban
        : "invalid iban",
      invitingAccount: invitingAccount
        ? getMappedName(invitingAccount, false)
        : getLocalizedText("system.rentio"),
      invoiceAmount: invoiceAmount
        ? formatCurrency(invoiceAmount)
        : "invalid amount",
      newStopDate: newStopDate
        ? formatDate(newStopDate)
        : "invalid new stop date",
      notificationType: notificationType
        ? getLocalizedText(
            `activity.notification_type.${notificationType}`,
          ).toLowerCase()
        : `unknown type: ${notificationType}`,
      oldStopDate: oldStopDate
        ? formatDate(oldStopDate)
        : "invalid old stop date",
      participant: participant
        ? getMappedName(participant, false)
        : "invalid participant",
      payee: payee ? getMappedName(payee, false) : "invalid payee",
      payer: payer ? getMappedName(payer, false) : "invalid payer",
      paymentRequestEstimatedCollectionDate,
      paymentRequestAmount: paymentRequest
        ? formatCurrency(paymentRequest.originalAmount)
        : "invalid amount",
      paymentRequestPeriod,
      paymentRequestInterface: paymentRequest?.paymentInterface
        ? getLocalizedText(
            `payment_request.interface.${paymentRequest.paymentInterface}`,
          )
        : "invalid payment interface",
      paymentRequestMethod: paymentRequest?.paymentMethod
        ? getLocalizedText(
            `payment_request.method.${paymentRequest.paymentMethod}`,
          )
        : "invalid payment method",
      paymentTransactionAmount: paymentTransaction
        ? formatCurrency(paymentTransaction.amount)
        : "invalid amount",
      paymentRequest: paymentRequestTitle,
      paymentType,
      paymentPayInPayerName: paymentPayIn?.authorName || "invalid author",
      paymentPayInPayerIban: paymentPayIn?.authorIban || "invalid author",
      paymentPayInRemainingBalance: paymentPayInRemainingBalance
        ? formatCurrency(paymentPayInRemainingBalance)
        : "invalid balance",
      property: property ? formatAddress(property) : "invalid property",
      linkedProperty: getLinkedProperty(contract),
      mainProperty: getMainProperty(contract),
      propertyInfo: propertyInfo
        ? propertyInfo.reference
        : "invalid property info",
      roles: roles
        ? roles
            .map(role => getLocalizedText(`role.${role.toLowerCase()}`))
            .join(", ")
        : "invalid roles",
      reference: reference ? reference : "no reference",
      rentAmount: rentAmount ? formatCurrency(rentAmount) : "invalid amount",
      source: source
        ? getLocalizedText(`source.${source.toLowerCase()}`)
        : "Rentio",
      step: step
        ? getLocalizedText(`property.dashboard.application.${step}.title`)
        : "invalid step",
      tacVersion: tacVersion || "invalid version",
      taskTitle: taskTitle || "invalid task",
      usedSigningType: usedSigningType
        ? getLocalizedText(`used_signing_type.${usedSigningType.toLowerCase()}`)
        : "invalid used signing type",
      leadId: lead ? lead.id : "invalid lead",
      leadAddress: lead ? formatAddressLead(lead) : "invalid lead",
      leadName: lead
        ? `${lead.contactFirstname} ${lead.contactLastname}`
        : "invalid lead",
      leadReferrer: lead ? lead.referrer : "invalid lead",
      leadEmail: lead ? lead.contactEmail : "invalid email",
      quoteValue: quote
        ? formatCurrency(quote.calculationValue)
        : "invalid quote",
      rentContractRejectionComment:
        rentContractRejectionComment || "invalid reason",
      locationDescriptionRejectionComment:
        locationDescriptionRejectionComment || "invalid reason",
      epcLabel,
      epcValue,
      epcExpirationDate,
      stopDate: stopDate
        ? formatDate(validStopDate(stopDate))
        : "invalid stop date",
      reasonForCancellation: reasonForCancellation
        ? getLocalizedText(`contract.cancellation.${reasonForCancellation}`)
        : "invalid reason",
      dateOfCancellation: dateOfCancellation
        ? formatDate(dateOfCancellation)
        : "invalid cancelled date",
    };

    return renderTouchableActivityText(
      activityItem,
      {
        amount: values.amount,
        application: values.application,
        applicationEmail: values.applicationEmail,
        asset: values.asset,
        broker: values.broker,
        contract: values.contract,
        contractNewPrice: values.contractNewPrice,
        contractOldPrice: values.contractOldPrice,
        contractRentDiscountPrice: values.contractRentDiscountPrice,
        createdAtDate: values.createdAtDate,
        createdAtDateAndTime: values.createdAtDateAndTime,
        createdAtTime: values.createdAtTime,
        dateOfCancellation: values.dateOfCancellation,
        email,
        epcExpirationDate: values.epcExpirationDate,
        epcLabel: values.epcLabel,
        epcValue: values.epcValue,
        iban: values.iban, // Deprecated => use payerBankAccount / payeeBankAccount
        invitingAccount: values.invitingAccount,
        invoiceAmount: values.invoiceAmount,
        leadAddress: values.leadAddress,
        leadEmail: values.leadEmail,
        leadId: values.leadId,
        leadName: values.leadName,
        leadReferrer: values.leadReferrer,
        locationDescriptionRejectionComment:
          values.locationDescriptionRejectionComment,
        newStopDate: values.newStopDate,
        notificationType: values.notificationType,
        oldStopDate: values.oldStopDate,
        payeeBankAccount: values.payeeBankAccount,
        payerBankAccount: values.payerBankAccount,
        paymentPayInPayerIban: values.paymentPayInPayerIban,
        paymentPayInPayerName: values.paymentPayInPayerName,
        paymentPayInRemainingBalance: values.paymentPayInRemainingBalance,
        paymentRequestAmount: values.paymentRequestAmount,
        paymentRequestEstimatedCollectionDate:
          values.paymentRequestEstimatedCollectionDate,
        paymentRequestInterface: values.paymentRequestInterface,
        paymentRequestMethod: values.paymentRequestMethod,
        paymentRequestPeriod: values.paymentRequestPeriod,
        paymentTransactionAmount: values.paymentTransactionAmount,
        propertyInfo: values.propertyInfo,
        quoteValue: values.quoteValue,
        reasonForCancellation: values.reasonForCancellation,
        reference: values.reference,
        rentAmount: values.rentAmount,
        rentContractRejectionComment: values.rentContractRejectionComment,
        roles: values.roles,
        source: values.source,
        step: values.step,
        stopDate: values.stopDate,
        tacVersion: values.tacVersion,
        usedSigningType: values.usedSigningType,
      },
      {
        actor: values.actor,
        contact: values.contact,
        documentFilename,
        documentPackage: values.documentPackage,
        linkedProperty: values.linkedProperty,
        mainProperty: values.mainProperty,
        participant: values.participant,
        payee: values.payee,
        payer: values.payer,
        paymentRequest: values.paymentRequest,
        paymentType: values.paymentType,
        property: values.property,
        taskTitle,
        visitUrl,
      },
    );
  };

  return (
    <Typography variant="subtitle2" sx={sx.wrapper}>
      {renderContent()}
    </Typography>
  );
};
